import { getModulePath, LEAGUE_MODULE_NAMES } from '@leagueplatform/core';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { usePPSystemId } from 'hooks/use-pp-system-id/use-pp-system-id.hook';
import { PATIENT_PORTAL_SYSTEM_IDS } from 'types/patient-portal-system.types';
import {
  clearBookingIframeSession,
  readInPersonLandingParams,
  stripBookingLinkFromLocationState,
} from 'utils/book-appointment-location-state';

export type UseBookingExitRoutingResult = {
  handleExit: (hasCompleted: boolean) => void;
};

/**
 * Owns where the user lands when they leave the booking iframe modal, and
 * clears the stored booking session on the way out.
 *
 * Requirements:
 * - Amplar, exiting before completing:
 *   - In-person booking -> handover (landing) screen.
 *   - Virtual booking -> handover (landing) screen.
 * - Amplar, exiting on complete:
 *   - In-person booking -> home page.
 *   - Virtual booking -> home page.
 * - MyHealth, exiting before completing or on complete:
 *   - In-person booking -> home page. (MyHealth has no virtual flow.)
 *
 * Implementation logic:
 * - Completed bookings, and every MyHealth flow, go to the home page.
 * - Amplar, modal opened in place over a landing page (virtual, or in-person
 *   via a masonry "book again" action that deep-links straight to the in-person
 *   landing page): closing in place keeps the current URL and query params
 *   intact.
 * - Amplar in-person via care-finder: the modal is opened from inside the
 *   care-finder module, so route back to the in-person landing page and restore
 *   its stored query params.
 */
export function useBookingExitRouting(): UseBookingExitRoutingResult {
  const navigate = useNavigate();
  const { pathname, search, state } = useLocation();
  const currentReturnPath = `${pathname}${search}`;
  const systemId = usePPSystemId();

  const handleExit = useCallback(
    (hasCompleted: boolean) => {
      const getExitDestination = () => {
        if (hasCompleted || systemId !== PATIENT_PORTAL_SYSTEM_IDS.AMPLAR) {
          return APP_MANAGED_ROUTES.HOME;
        }

        // Modal opened in place over a landing page (virtual, or in-person via
        // a "book again" deep link): close in place, keeping the current
        // URL/params.
        if (
          pathname === APP_MANAGED_ROUTES.VIRTUAL_CONSULTATION ||
          pathname === APP_MANAGED_ROUTES.IN_PERSON_CONSULTATION
        ) {
          return currentReturnPath;
        }

        // New in-person booking flow: the modal was opened from inside the
        // care-finder module, so route back to the in-person landing page and
        // restore its stored query params.
        const careFinderPath = getModulePath(LEAGUE_MODULE_NAMES.careFinder);
        if (pathname.startsWith(careFinderPath)) {
          return `${
            APP_MANAGED_ROUTES.IN_PERSON_CONSULTATION
          }${readInPersonLandingParams()}`;
        }

        return APP_MANAGED_ROUTES.HOME;
      };

      clearBookingIframeSession();
      const cleanedState = stripBookingLinkFromLocationState(state);
      const destination = getExitDestination();

      navigate(destination, {
        replace: true,
        ...(cleanedState !== undefined && { state: cleanedState }),
      });
    },
    [navigate, pathname, state, systemId, currentReturnPath],
  );

  return { handleExit };
}
